import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { startWith } from 'rxjs';
import type { AnimationItem } from 'lottie-web';
import { AuthService } from '../../services/auth';
import {
  OnboardingService,
  SelfOnboardPayload,
  SelfOnboardResponse,
} from '../../services/onboarding';
import { ToastService } from '../../services/toast';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.html',
  styleUrl: './onboarding.css',
  imports: [ReactiveFormsModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Onboarding {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly onboardingService = inject(OnboardingService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private successAnimation: AnimationItem | null = null;
  private dashboardDomain = '';
  private dashboardStaffId = '';
  private dashboardToken = '';

  @ViewChild('successAnimation')
  set successAnimationContainer(container: ElementRef<HTMLElement> | undefined) {
    this.successAnimation?.destroy();
    this.successAnimation = null;

    if (!container || !this.isBrowser) {
      return;
    }

    void import('lottie-web').then(({ default: lottie }) => {
      if (!this.setupComplete()) {
        return;
      }

      this.successAnimation = lottie.loadAnimation({
        container: container.nativeElement,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: '/success-animation.json?v=5',
      });
    });
  }

  readonly form = this.fb.group({
    businessName: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^[a-zA-Z0-9 ]+$/)]],
    businessEntityName: ['', [Validators.required, Validators.minLength(2)]],
    businessAddressLine1: ['', Validators.required],
    businessAddressLine2: [''],
    businessCity: ['', Validators.required],
    businessState: ['', Validators.required],
    sameAddressForStore: [true],
    storeName: [''],
    storeAddressLine1: [''],
    storeAddressLine2: [''],
    storeCity: [''],
    storeState: [''],
    googleMapsLink: [''],
  });

  readonly isSubmitting = signal(false);
  readonly setupComplete = signal(false);
  readonly createdBusiness = signal({
    businessName: '',
    legalEntityName: '',
    storeName: '',
    location: '',
  });

  private readonly formTick = toSignal(this.form.valueChanges.pipe(startWith(null)), {
    initialValue: null,
  });

  readonly showStoreDetails = computed(() => {
    this.formTick();
    return !this.form.controls.sameAddressForStore.value;
  });

  readonly canSubmit = computed(() => {
    this.formTick();
    this.isSubmitting();
    return this.isFormValid() && !this.isSubmitting();
  });

  submit(): void {
    if (!this.isFormValid()) {
      this.markRequiredFieldsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.onboardingService.selfOnboard(this.buildPayload()).subscribe({
      next: (response) => {
        const form = this.form.getRawValue();
        const sameAddress = !this.showStoreDetails();
        this.isSubmitting.set(false);
        this.dashboardDomain = this.getDashboardDomain(response);
        this.dashboardStaffId = response.data?.staff?.staffId?.trim() ?? '';
        this.dashboardToken =
          response.data?.staff?.token?.trim() ||
          this.auth.getStoredProfile()?.token ||
          '';
        this.createdBusiness.set({
          businessName: form.businessName?.trim() ?? '',
          legalEntityName: form.businessEntityName?.trim() ?? '',
          storeName: sameAddress
            ? (form.businessName?.trim() ?? '')
            : (form.storeName?.trim() ?? ''),
          location: sameAddress
            ? [form.businessCity, form.businessState].filter(Boolean).join(', ')
            : [form.storeCity, form.storeState].filter(Boolean).join(', '),
        });
        this.setupComplete.set(true);
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.toast.error(this.extractApiError(error));
      },
    });
  }

  closeSuccess(): void {
    this.successAnimation?.destroy();
    this.successAnimation = null;
    this.setupComplete.set(false);
    this.dashboardDomain = '';
    this.dashboardStaffId = '';
    this.dashboardToken = '';
    this.createdBusiness.set({
      businessName: '',
      legalEntityName: '',
      storeName: '',
      location: '',
    });
    this.form.reset({ sameAddressForStore: true });
    this.auth.logout();
    void this.router.navigate(['/login']);
  }

  logout(): void {
    this.auth.logout();
    void this.router.navigate(['/login']);
  }

  onBusinessNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/[^a-zA-Z0-9 ]/g, '').replace(/^\s+/, '');
    if (sanitized === input.value) {
      return;
    }

    this.form.controls.businessName.setValue(sanitized);
    input.value = sanitized;
  }

  onBusinessNameBlur(): void {
    const trimmed = this.form.controls.businessName.value?.trim() ?? '';
    this.form.controls.businessName.setValue(trimmed);
  }

  goToDashboard(): void {
    if (!this.dashboardDomain) {
      this.toast.error('Dashboard URL was not provided. Please log in again.');
      this.closeSuccess();
      return;
    }

    if (!this.dashboardStaffId) {
      this.toast.error('Staff ID was not provided. Please log in again.');
      return;
    }

    const domainUrl = /^https?:\/\//i.test(this.dashboardDomain)
      ? this.dashboardDomain
      : `https://${this.dashboardDomain}`;
    const url = new URL(domainUrl);
    url.searchParams.set('staffId', this.dashboardStaffId);
    url.searchParams.set('isOnboarding', 'true');
    if (this.dashboardToken) {
      url.searchParams.set('token', this.dashboardToken);
    }
    const dashboardWindow = window.open(url.toString(), '_blank');

    if (!dashboardWindow) {
      this.toast.error('Please allow pop-ups to open the dashboard.');
      return;
    }

    try {
      dashboardWindow.opener = null;
    } catch {
      // The dashboard is already opening in its isolated tab.
    }

    this.closeSuccess();
  }

  private isFormValid(): boolean {
    const businessValid =
      this.form.controls.businessName.valid &&
      this.form.controls.businessEntityName.valid &&
      this.form.controls.businessAddressLine1.valid &&
      this.form.controls.businessCity.valid &&
      this.form.controls.businessState.valid;

    if (!businessValid) {
      return false;
    }

    if (!this.showStoreDetails()) {
      return true;
    }

    return (
      !!this.form.controls.storeName.value?.trim() &&
      !!this.form.controls.storeAddressLine1.value?.trim() &&
      !!this.form.controls.storeCity.value?.trim() &&
      !!this.form.controls.storeState.value?.trim()
    );
  }

  private buildPayload(): SelfOnboardPayload {
    const form = this.form.getRawValue();
    const profile = this.auth.getStoredProfile();
    const sameAddress = !this.showStoreDetails();

    const businessAddress = {
      country: 'India',
      state: form.businessState?.trim() ?? '',
      city: form.businessCity?.trim() ?? '',
      locality: form.businessAddressLine2?.trim() ?? '',
      addressLine: form.businessAddressLine1?.trim() ?? '',
      addressDescription: '',
      pinCode: 0,
      geoLocation: { latitude: 0, longitude: 0 },
    };

    const storeAddress = sameAddress
      ? { ...businessAddress }
      : {
          country: 'India',
          state: form.storeState?.trim() ?? '',
          city: form.storeCity?.trim() ?? '',
          locality: form.storeAddressLine2?.trim() ?? '',
          addressLine: form.storeAddressLine1?.trim() ?? '',
          addressDescription: form.googleMapsLink?.trim() ?? '',
          pinCode: 0,
          geoLocation: { latitude: 0, longitude: 0 },
        };

    return {
      store: {
        storeName: sameAddress
          ? (form.businessName?.trim() ?? '')
          : (form.storeName?.trim() ?? ''),
        addressDetails: storeAddress,
        storeContactDetails: {
          countryCode: profile?.countryCode ?? '91',
          mobile: profile?.mobile ?? '',
          email: profile?.email ?? '',
          contactPerson: profile?.name ?? '',
        },
      },
      businessName: form.businessName?.trim() ?? '',
      businessEntityName: form.businessEntityName?.trim() ?? '',
      addressDetails: businessAddress,
      mobile: profile?.mobile ?? '',
      name: '',
      email: profile?.email ?? '',
    };
  }

  private extractApiError(error: HttpErrorResponse): string {
    const body = error.error;
    if (body && typeof body === 'object') {
      const message = body.message ?? body.error ?? body.errorMessage;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (error.status === 0) {
      return 'Unable to reach the server. Please check your connection and try again.';
    }
    return 'Something went wrong. Please try again.';
  }

  private getDashboardDomain(response: SelfOnboardResponse): string {
    return (
      response.data?.business?.erpDomainName?.trim() ||
      response.data?.erpDomainName?.trim() ||
      response.erpDomainName?.trim() ||
      ''
    );
  }

  private markRequiredFieldsTouched(): void {
    this.form.controls.businessName.markAsTouched();
    this.form.controls.businessEntityName.markAsTouched();
    this.form.controls.businessAddressLine1.markAsTouched();
    this.form.controls.businessCity.markAsTouched();
    this.form.controls.businessState.markAsTouched();

    if (this.showStoreDetails()) {
      this.form.controls.storeName.markAsTouched();
      this.form.controls.storeAddressLine1.markAsTouched();
      this.form.controls.storeCity.markAsTouched();
      this.form.controls.storeState.markAsTouched();
    }
  }
}
